import { forkJoin as observableForkJoin } from 'rxjs';
import { Component, ChangeDetectionStrategy } from '@angular/core';

import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService } from '../shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';

import { EdifactConfigService } from './services/edifact-config.service';
import { X12ConfigService } from './services/x12-config.service';
import { EdiConfigHelperService } from './services/edi-configuration-helper.service';
import { PartnerIdentificationTypeService } from './services/partner-identification-type.service';

import { EdiConfig } from './models/edifact-config';
import { X12Config } from './models/x12-config';
import { EdifactSegmentSuffix } from './models/edifact-segment-suffix';
import { EdifactCharacterSet } from './models/edifact-character-set';
import { EdifactSyntax } from './models/edifact-syntax';
import { PartnerIdentificationType } from './models/partner-identification-type';
import { PartnerComponentBase } from './partner-search-details.base';

import { X12AuthorizationQualifier } from './models/x12-authorization-qualifier';
import { X12CharacterSet } from './models/x12-character-set';
import { X12SecurityQualifier } from './models/x12-security-qualifier';
import { ConfigurationService } from '../configuration/services/configuration.service';
import { FormsModule } from '@angular/forms';

import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';

@Component({
    templateUrl: './partner-search-details-configuration.component.html',
    selector: 'ep-partner-details-configurations',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgbTooltip, SvgIconComponent, PermissionRequiredDirective]
})

export class PartnerSearchDetailsConfigurationComponent extends PartnerComponentBase {

    constructor(private ediConfigHelperService: EdiConfigHelperService, private toastService: ToastService, private edifactConfigService: EdifactConfigService, private x12ConfigService: X12ConfigService, private partnerIdentificationService: PartnerIdentificationTypeService, private modalService: GenericModalService, private loadingPanelService: LoadingPanelService, private configService: ConfigurationService) { super(); }

    public edifactCharacterSets: EdifactCharacterSet[] = [];
    public edifactSyntaxes: EdifactSyntax[] = [];
    public partnerIdentificationTypes: PartnerIdentificationType[] = [];
    public ediSegmentSuffixes: EdifactSegmentSuffix[] = [];
    public priorityCodes: string[] = [];
    public passwordQualifiers: string[] = [];

    public authorizationQualifiers: X12AuthorizationQualifier[] = [];
    public securityQualifiers: X12SecurityQualifier[] = [];
    public characterSets: X12CharacterSet[] = [];

    public clonedEdiConfig: EdiConfig;
    public clonedX12Config: X12Config;
    public isEditing: boolean = false;

    async partnerSet(): Promise<void> {
        await this.initConfig();
        this.cloneConfigs();
    }

    async initConfig(): Promise<void> {
        const partnerId = this.selectedPartner?.PartnersId;
        const [
            partnerIdTypes,
            ediConfigContainer,
        ] = await Promise.all([
            partnerId ? this.partnerIdentificationService.getPartnerIdentificationTypes(partnerId) : [] as PartnerIdentificationType[],
            this.ediConfigHelperService.loadConfigurations(),
        ]);

        this.partnerIdentificationTypes = partnerIdTypes.sort((t1, t2) => (t1.IdentificationType.Name + t1.Value).localeCompare(t2.IdentificationType.Name + t2.Value));
        this.ediSegmentSuffixes = ediConfigContainer.ediSegmentSuffixes.sort((s1, s2) => s1.SegmentSuffixName.localeCompare(s2.SegmentSuffixName));
        this.edifactCharacterSets = ediConfigContainer.edifactCharacterSets.sort((c1, c2) => c1.Identifier.localeCompare(c2.Identifier));
        this.edifactSyntaxes = ediConfigContainer.edifactSyntaxes.sort((s1, s2) => s1.Version.localeCompare(s2.Version));
        this.authorizationQualifiers = ediConfigContainer.authorizationQualifiers.sort((q1, q2) => q1.DisplayName.localeCompare(q2.DisplayName));
        this.characterSets = ediConfigContainer.characterSets.sort((c1, c2) => c1.Name.localeCompare(c2.Name));
        this.securityQualifiers = ediConfigContainer.securityQualifiers.sort((sq1, sq2) => sq1.DisplayName.localeCompare(sq2.DisplayName));
    }

    cloneConfigs() {
        if (this.selectedPartner) {
            // Edifact config
            this.selectedPartner.EdiConfig ??= new EdiConfig();
            this.clonedEdiConfig = Object.assign({}, this.selectedPartner.EdiConfig);
            if (this.clonedEdiConfig.CharacterSet) {
                this.clonedEdiConfig.CharacterSet = this.edifactCharacterSets.find((s) => s.EdifactCharacterSetId == this.clonedEdiConfig.CharacterSet.EdifactCharacterSetId);
            }
            if (this.clonedEdiConfig.Syntax) {
                this.clonedEdiConfig.Syntax = this.edifactSyntaxes.find((s) => s.EdifactSyntaxId == this.clonedEdiConfig.Syntax.EdifactSyntaxId);
            }
            if (this.clonedEdiConfig.SegmentSuffix) {
                this.clonedEdiConfig.SegmentSuffix = this.ediSegmentSuffixes.find((s) => s.EdifactSegmentSuffixId == this.clonedEdiConfig.SegmentSuffix.EdifactSegmentSuffixId);
            }
            if (this.clonedEdiConfig.PartnerIdentificationType) {
                this.clonedEdiConfig.PartnerIdentificationType = this.partnerIdentificationTypes.find((pi) => pi.PartnerIdentificationTypeId == this.clonedEdiConfig.PartnerIdentificationType.PartnerIdentificationTypeId);
            }

            this.clonedEdiConfig.NewSequenceValue = this.clonedEdiConfig.CurrentSequenceValue; // Set new SequenceValue to current value so we can bind the NewSequenceValue

            // X12
            this.selectedPartner.X12Config ??= new X12Config();
            this.clonedX12Config = Object.assign({}, this.selectedPartner.X12Config);
            if (this.clonedX12Config.AuthorizationQualifier) {
                this.clonedX12Config.AuthorizationQualifier = this.authorizationQualifiers.find((aq) => aq.X12AuthorizationQualifierId == this.clonedX12Config.AuthorizationQualifier.X12AuthorizationQualifierId);
            }
            if (this.clonedX12Config.CharacterSet) {
                this.clonedX12Config.CharacterSet = this.characterSets.find((cs) => cs.X12CharacterSetId == this.clonedX12Config.CharacterSet.X12CharacterSetId);
            }
            if (this.clonedX12Config.EdifactSegmentSuffix) {
                this.clonedX12Config.EdifactSegmentSuffix = this.ediSegmentSuffixes.find((s) => s.EdifactSegmentSuffixId == this.clonedX12Config.EdifactSegmentSuffix.EdifactSegmentSuffixId);
            }
            if (this.clonedX12Config.PartnerIdentificationType) {
                this.clonedX12Config.PartnerIdentificationType = this.partnerIdentificationTypes.find((pi) => pi.PartnerIdentificationTypeId == this.clonedX12Config.PartnerIdentificationType.PartnerIdentificationTypeId);
            }
            if (this.clonedX12Config.SecurityQualifier) {
                this.clonedX12Config.SecurityQualifier = this.securityQualifiers.find((sq) => sq.X12SecurityQualifierId == this.clonedX12Config.SecurityQualifier.X12SecurityQualifierId);
            }
        }
        else {
            this.clonedEdiConfig = new EdiConfig();
            this.clonedX12Config = new X12Config();
        }
    }

    edit() {
        this.setEditMode(true);
        this.isEditing = true;
    }

    cancel() {
        this.cloneConfigs();
        this.setEditMode(false);
        this.isEditing = false;
    }

    async save(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Saving EDI configuration');
            this.selectedPartner.EdiConfig = await this.edifactConfigService.saveEdifactConfig(this.clonedEdiConfig, this.selectedPartner.PartnersId);
            this.selectedPartner.X12Config = await this.x12ConfigService.saveEdifactConfig(this.clonedX12Config, this.selectedPartner.PartnersId);
            this.toastService.showToast('EDI configurations saved...', ToastType.success);
        }
        catch (err) {
        }
        finally {
            this.cancel(); // Let cancel do the rest (clone object, set edit-mode, etc.)
            this.loadingPanelService.hideLoadingPanel();
        }
    }
}
